import React, {useContext, useEffect, useState} from 'react'
import { Box, Button, Container, Hidden, IconButton, List, ListItem, ListItemText, Tab, Tabs, Typography, Grid, Avatar, Divider, useMediaQuery, useTheme, Menu, MenuItem } from '@material-ui/core'
import ArrowForwardIcon from '@material-ui/icons/ChevronRight'
import ArrowBackIcon from '@material-ui/icons/ChevronLeft'
import DotIcon from '@material-ui/icons/FiberManualRecord'
import MenuIcon from '@material-ui/icons/Menu'

import { useStyles } from 'styles/styles'
import { useTextLookup } from 'utils/text'
import AppContext from 'components/app/AppContext'
import AppHeader from 'components/widgets/AppHeader'
import ContentPanel from 'components/content/ContentPanel'
import Topic from 'models/Topic'
import Feature from 'models/Feature'

interface TopicContentPageProps {
    topic: Topic
    onSelectTopic: (topicId: number) => void
}

const TopicContentPage: React.FC<TopicContentPageProps> = (props) => {
    const appContext = useContext(AppContext)
    const _t = useTextLookup()
    const classes = useStyles()
    const theme = useTheme()
    const md = useMediaQuery('(min-width:960px)')
    const [currentFeature, setCurrentFeature] = useState(props.topic.features[0])
    const [allMarketFeatures, setAllMarketFeatures] = useState<Feature[]>([])
    const [allMarketStep, setAllMarketStep] = useState(0)
    const [selectMarketFeatures, setSelectMarketFeatures] = useState<Feature[]>([])
    const [selectMarketStep, setSelectMarketStep] = useState(-1)
    const [doSelectLastFeature, setDoSelectLastFeature] = useState(false)
    const [isFirstFeature, setIsFirstFeature] = useState(false)
    const [isLastFeature, setIsLastFeature] = useState(false)
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null)
    
    // Split features of current topic into all market and select market 
    useEffect(() => {
        if (props.topic != null) {
            let all: Feature[] = []
            let select: Feature[] = []
            // console.log("Selected region IDs", appContext.regionFilterIds)
            props.topic.features.forEach(feature => {
                if (appContext.regionFilterIds.every(id => feature.regionIDs.includes(id))) {
                    // console.log(feature.regionIDs, "All")
                    all.push(feature)
                } else if (appContext.regionFilterIds.some(id => feature.regionIDs.includes(id))) {
                    // console.log(feature.regionIDs, "Select")
                    select.push(feature)
                }
            })

            setAllMarketFeatures(all)
            setSelectMarketFeatures(select)
        }
    }, [appContext.topics, appContext.regionFilterIds, props.topic])

    useEffect(() => {
        if (allMarketFeatures.length > 0 || selectMarketFeatures.length > 0) {
            if (doSelectLastFeature) {
                if (selectMarketFeatures.length > 0) {
                    setAllMarketStep(-1)
                    setSelectMarketStep(selectMarketFeatures.length - 1)
                    setCurrentFeature(selectMarketFeatures[selectMarketFeatures.length - 1])
                } else {
                    setSelectMarketStep(-1)
                    setAllMarketStep(allMarketFeatures.length - 1)
                    setCurrentFeature(allMarketFeatures[allMarketFeatures.length - 1])
                }
            } else {
                if (allMarketFeatures.length > 0) {
                    setAllMarketStep(0)
                    setSelectMarketStep(-1)
                    setCurrentFeature(allMarketFeatures[0])
                } else {
                    setAllMarketStep(-1)
                    setSelectMarketStep(0)
                    setCurrentFeature(selectMarketFeatures[0])
                }
            }
        }
    }, [allMarketFeatures, selectMarketFeatures, doSelectLastFeature])

    useEffect(() => {
        const firstFeature = allMarketFeatures.length > 0 ? allMarketFeatures[0] : selectMarketFeatures.length > 0 ? selectMarketFeatures[0] : appContext.topics[0].features[0]
        setIsFirstFeature(props.topic.id === appContext.topics[0].id && currentFeature.id === firstFeature.id)
        
        const lastTopic = appContext.topics[appContext.topics.length - 1]
        const lastFeature = selectMarketFeatures.length > 0 ? selectMarketFeatures[selectMarketFeatures.length - 1] : allMarketFeatures.length > 0 ? allMarketFeatures[allMarketFeatures.length - 1] : lastTopic.features[lastTopic.features.length - 1]
        setIsLastFeature(props.topic.id === lastTopic.id && currentFeature.id === lastFeature.id)
    }, [props.topic, appContext.topics, currentFeature, allMarketFeatures, selectMarketFeatures])

    const goToTopic = (topicId: number, lastFeature: boolean = false) => {
        props.onSelectTopic(topicId)
        setDoSelectLastFeature(lastFeature)
    }

    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setMenuAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setMenuAnchorEl(null)
    }

    const handleMenuItemClick = (value: number) => {
        goToTopic(value)
    }

    const handleHomeButton = () => {
        appContext.onNavigate(0)
    }

    const handleTabChange = (event: React.ChangeEvent<{}>, value: number) => {
        goToTopic(value)
    }

    const handleNextStep = () => {
        if (allMarketFeatures.length > 0 && allMarketStep !== -1) {
            const nextStep = allMarketStep + 1
            if (nextStep >= allMarketFeatures.length) {
                if (selectMarketFeatures.length > 0) {
                    // Go to first select market feature
                    setSelectMarketStep(0)
                    setAllMarketStep(-1)
                    setCurrentFeature(selectMarketFeatures[0])
                } else {
                    // Go to next topic
                    const currentTopicIndex = appContext.topics.indexOf(props.topic)
                    const nextTopic = appContext.topics[currentTopicIndex + 1]
                    goToTopic(nextTopic.id)
                }
            } else {
                // Go to next all market feature
                setAllMarketStep(nextStep)
                setCurrentFeature(allMarketFeatures[nextStep])
            }
        } else {
            const nextStep = selectMarketStep + 1
            if (nextStep >= selectMarketFeatures.length) {
                // Go to next topic
                const currentTopicIndex = appContext.topics.indexOf(props.topic)
                const nextTopic = appContext.topics[currentTopicIndex + 1]
                goToTopic(nextTopic.id)
            } else {
                // Go to next select market feature
                setSelectMarketStep(nextStep)
                setCurrentFeature(selectMarketFeatures[nextStep])
            }
        }
    }

    const handlePreviousStep = () => {
        if (allMarketFeatures.length > 0 && allMarketStep !== -1) {
            const previousStep = allMarketStep - 1
            if (previousStep < 0) {
                // Go to previous topic
                const currentTopicIndex = appContext.topics.indexOf(props.topic)
                const previousTopic = appContext.topics[currentTopicIndex - 1]
                goToTopic(previousTopic.id, true)
            } else {
                // Go to previous all market feature
                setAllMarketStep(previousStep)
                setCurrentFeature(allMarketFeatures[previousStep])
            }
        } else {
            const previousStep = selectMarketStep - 1
            if (previousStep < 0) {
                if (allMarketFeatures.length > 0) {
                    // Go to to last all market feature
                    setAllMarketStep(allMarketFeatures.length - 1)
                    setSelectMarketStep(-1)
                    setCurrentFeature(allMarketFeatures[allMarketFeatures.length - 1])
                } else {
                    // Go to previous topic
                    const currentTopicIndex = appContext.topics.indexOf(props.topic)
                    const previousTopic = appContext.topics[currentTopicIndex - 1]
                    goToTopic(previousTopic.id, true)
                }
            } else {
                // Go to previous select market feature
                setSelectMarketStep(previousStep)
                setCurrentFeature(selectMarketFeatures[previousStep])
            }
        }
    }

    const handleStep = (step: number, allMarkets: boolean) => {
        if (allMarkets) {
            setAllMarketStep(step)
            setSelectMarketStep(-1)
            setCurrentFeature(allMarketFeatures[step])
        } else {
            setSelectMarketStep(step)
            setAllMarketStep(-1)
            setCurrentFeature(selectMarketFeatures[step])
        }
    }

    const markets = []
    if (allMarketFeatures.length > 0) {
        markets.push(
            {
                name: _t("topic_content.all_markets"),
                all: true,
                activeStep: allMarketStep,
                features: allMarketFeatures
            }
        )
    }
    if (selectMarketFeatures.length > 0) {
        markets.push(
            {
                name: _t("topic_content.select_markets"),
                all: false,
                activeStep: selectMarketStep,
                features: selectMarketFeatures
            }
        )
    }

    const dotColor = (index: number, activeStep: number) => {
        return index === activeStep ? theme.palette.primary.main : theme.palette.common.white
    }

    return (
        <Box component="main">
            <Box className={classes.pageHeading}>
                <Container maxWidth="lg">
                    <AppHeader />
                    <Hidden xsDown>
                        <Tabs
                            value={props.topic.id}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="on"
                            indicatorColor="primary"
                            textColor="primary"
                            aria-label="Menubar"
                        >
                            {appContext.topics.map(feature =>
                                <Tab 
                                    key={feature.id} 
                                    style={{ textTransform: 'none' }} 
                                    label={feature.name} 
                                    value={feature.id} 
                                    icon={<img className={classes.tabIcon} src={feature.image} alt="" />} 
                                    wrapped
                                />
                            )}
                        </Tabs>
                    </Hidden>
                    <Hidden smUp>
                        <Box style={{position: 'absolute', top: '1rem', right: '1rem'}}>
                            <IconButton onClick={handleMenuClick}>
                                <MenuIcon />
                            </IconButton>
                            <Menu 
                                anchorEl={menuAnchorEl}
                                open={Boolean(menuAnchorEl)}
                                onClose={handleMenuClose}
                            >
                                {appContext.topics.map(feature =>
                                    <MenuItem key={feature.id} onClick={e => handleMenuItemClick(feature.id)}>
                                        <img className={classes.tabIcon} src={feature.image} alt="" /> 
                                        <ListItemText>{feature.name}</ListItemText>
                                    </MenuItem>
                                )}
                            </Menu>
                        </Box>
                    </Hidden>
                    <Divider className={classes.menuDivider} />
                    <Box display="flex" alignItems="center">
                        <img style={{ height: '56px' }} src={props.topic.image} alt="" />
                        <Typography variant='h3' color='primary'>&nbsp;{props.topic.name}</Typography>
                    </Box>
                    <Typography className={classes.paragraph}>{props.topic.content}</Typography>
                </Container>
            </Box>
            <Box>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        <Hidden xsDown>
                            <Grid item xs={12} sm={3}>
                                <Box className={`${classes.paragraph} ${classes.verticalSeparator}`}>
                                    <Typography variant="h5" color="primary">{_t("topic_content.side_title")}</Typography>
                                    {markets.map((market, listIndex) =>
                                        <React.Fragment key={market.name}>
                                            <Typography className={classes.paragraph} variant="h6">{market.name}:</Typography>
                                            <List dense={true}>
                                                {market.features.map((feature, index) => 
                                                    <ListItem key={index} style={{ paddingLeft: 0, cursor: 'pointer' }}>
                                                        <DotIcon style={{ marginRight: 8, color: dotColor(index, market.activeStep) }} fontSize="small" />
                                                        <ListItemText 
                                                            primary={feature.name}
                                                            primaryTypographyProps={{
                                                                color: index === market.activeStep ? "primary" : "secondary"
                                                            }}
                                                            onClick={() => handleStep(index, market.all)}
                                                        />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </React.Fragment>
                                    )}
                                    <Typography className={classes.paragraph} variant="h6">{_t("topic_content.side_title2")}</Typography>
                                    <Grid container spacing={1}>
                                        {appContext.regionFilter.map((region) =>
                                            currentFeature.regionIDs.includes(region.id) &&
                                            <Grid item key={region.id} className={classes.marketBar}>
                                                <Avatar className={classes.market1}>
                                                    <Typography variant="body1">{region.code}</Typography>
                                                </Avatar>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </Grid>
                        </Hidden>
                        <Grid item xs={12} sm={9} >
                            <ContentPanel feature={currentFeature}/>
                            <Box className={classes.buttonBar2} display="flex" flexDirection={md ? "row" : "column"}>
                                <Button disabled={isFirstFeature} variant="outlined" onClick={handlePreviousStep}><ArrowBackIcon />{_t("topic_content.back_button")}</Button>
                                <Button className={classes.navButton} variant="contained" onClick={handleHomeButton}>{_t("topic_content.home_button")}</Button>
                                <Button disabled={isLastFeature} variant="outlined" onClick={handleNextStep}>{_t("topic_content.next_button")}<ArrowForwardIcon /></Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    )
}

export default TopicContentPage
